import * as Sentry from '@sentry/react';

import { AuthError, ValidationError, HttpError } from "../../helpers/custom-errors";
import { isArrayWithLength } from "../../helpers/commons";
import log from 'loglevel';

const __BACKEND_API_PREFIX = process.env.REACT_APP_API_URL ?? "/api/v1";

export const appendPrefix = (path) => {
	return __BACKEND_API_PREFIX + path;
}

/* ── CSRF: o token é lido do COOKIE, no instante do envio ─────────────────────

   🔴 NÃO GUARDE CÓPIA DESTE VALOR. Em lugar nenhum: nem em estado do React, nem
   em módulo, nem em parâmetro de função de API. Foi a cópia que causou o defeito
   de 21/09, e a razão não é estilo — é mecânica do Play:

   Com `token.sign = true` (nosso caso) o Play randomiza a ASSINATURA a cada
   emissão e compara o valor SUBJACENTE (`compareTokens` → `compareSignedTokens`).
   ⇒ RE-ASSINATURA não invalida nada: duas strings diferentes do mesmo subjacente
   validam. Mas REEMISSÃO — subjacente novo — invalida, e permanentemente. E o
   Play reemite sempre que chega requisição SEM cookie de token.

   🔴 O boot do portal faz QUATRO chamadas token-less em paralelo (`/config` e
   `/auth`, repetidas). Medido no lab em 21/09, em Chrome anônimo: quatro
   emissões, quatro subjacentes distintos, e o cookie que sobreviveu foi o
   TERCEIRO — nem o primeiro, nem o último, nem o do `/config`. Não há ordem
   previsível, e nenhuma correção pode depender de qual resposta chega quando.
   Quem guardasse uma cópia guardaria o subjacente errado e tomaria 403 na
   primeira escrita.

   ⚠️ O defeito é de PRIMEIRA VISITA, não do WhatsApp nem do WebKit — jar de
   cookie vazio em qualquer navegador. O botão do template só garante essa
   condição a cada toque; o Chrome do dia a dia chega com jar quente, nada é
   reemitido, e nada diverge. Por isso ele "nunca falhava".

   ✅ Lendo o cookie na hora, a reemissão deixa de ser fatal e passa a ser
   invisível: a chamada seguinte já lê o valor novo. O defeito se auto-cura.   */

/* Métodos que o Play considera seguros e NÃO submete ao filtro de CSRF.
   ⚠️ Falhe PARA o lado de mandar o cabeçalho: manda-se em tudo que não estiver
   nesta lista. Cabeçalho a mais é ruído; cabeçalho a menos é 403. */
const CSRF_SAFE_METHODS = ["GET", "HEAD", "OPTIONS", "TRACE"];

/* Mesmo nome em `conf/application.conf` (`play.filters.csrf.cookie.name`) e no
   cabeçalho — `Csrf-Token` é o default do Play para os dois. */
const CSRF_COOKIE_NAME = "Csrf-Token";

/* ⚠️ `decodeURIComponent` é obrigatório: valor de cookie pode chegar
   percent-encoded, e uma comparação que falha por `%3D` é INDISTINGUÍVEL de
   token divergente no log do servidor — manda o diagnóstico para o lado errado.
   O cookie não é `HttpOnly` (default do Play para o de CSRF) justamente para
   que este código o leia. */
const readCsrfCookie = () => {
	if (typeof document === "undefined" || !document.cookie) return null;

	const prefix = CSRF_COOKIE_NAME + "=";
	for (const part of document.cookie.split(";")) {
		const entry = part.trim();
		if (!entry.startsWith(prefix)) continue;
		const raw = entry.slice(prefix.length);
		try {
			return decodeURIComponent(raw) || null;
		} catch (err) {
			/* Valor malformado: devolve cru em vez de derrubar a requisição —
			   o servidor recusa e a recusa aparece nomeada abaixo. */
			log.error("[csrf] malformed cookie value: " + err);
			return raw || null;
		}
	}
	return null;
};

/* ── Semear o cookie antes da PRIMEIRA escrita ────────────────────────────────

   🔴 O documento do portal NÃO semeia o cookie. Medido em 21/09: em staging e
   produção o `index.html` é servido pelo NGINX, do disco
   (`root /var/www/reeduca/portal/current; try_files ... /index.html`), e só
   `/api/v1/` é encaminhado ao Play. ⇒ a resposta do documento nunca passa pelo
   filtro de CSRF, e o portal SEMPRE inicia com o jar vazio. No lab é igual: o
   servidor do CRA (:5050) também responde sem `Set-Cookie`.
   ⚠️ Só o `GET /` direto no Play (:9000) semeia — e ninguém entra por ali.

   ⇒ Ler o cookie na hora resolve a divergência, mas não cobre a PRIMEIRA
   escrita: se o usuário tocar em algo antes de o boot terminar, não há cookie
   para ler e o Play recusa com 403. Não é hipotético — é o caso do link do
   WhatsApp, onde a entrada é sempre jar frio.

   ✅ A garantia mora aqui: escrita sem cookie SEMEIA primeiro (um `GET` no
   `/config`, que o Play responde com o `Set-Cookie`) e só então envia.

   🔴 Semear só acontece quando NÃO HÁ cookie — e é isso que o torna seguro.
   Requisição token-less faz o Play emitir subjacente NOVO; com o jar vazio não
   há nada para invalidar. Semear com cookie presente é que reabriria o defeito.

   ⚠️ UMA semeadura por vez, compartilhada. Sem isto, N escritas simultâneas num
   jar frio disparariam N `GET /config` token-less — N emissões, N subjacentes,
   e a corrida que o ciclo inteiro fechou voltaria por esta porta.               */

const CSRF_SEED_PATH = "/config";

let csrfSeeding = null;      /* promessa em voo, compartilhada */
let csrfSeedUseless = false; /* o navegador respondeu e MESMO ASSIM não guardou */

const seedCsrfCookie = async () => {
	if (csrfSeedUseless) return null;

	if (!csrfSeeding) {
		csrfSeeding = fetch(appendPrefix(CSRF_SEED_PATH), { method: "GET" })
			.then((response) => {
				/* ⚠️ Desistir só quando a resposta CHEGOU e o cookie não apareceu —
				   aí é o navegador que não guarda (cookies bloqueados, jar
				   particionado) e repetir só dobraria o tráfego. Falha de rede NÃO
				   desiste: pode ser transitória. */
				if (response && response.ok && !readCsrfCookie()) {
					csrfSeedUseless = true;
					log.error("[csrf] browser did not store the token cookie");
				}
			})
			.catch((err) => { log.error("[csrf] seeding request failed: " + err); })
			.finally(() => { csrfSeeding = null; });
	}

	await csrfSeeding;
	return readCsrfCookie();
};

/* Envia UMA vez, resolvendo o token no instante do envio. */
const sendWithCsrf = async (path, options, method) => {
	const opts = Object.assign({}, options);

	/* Cookie presente é o caso normal e não custa nada — leitura síncrona.
	   Ausente, semeia UMA vez antes de enviar. */
	const token = readCsrfCookie() || await seedCsrfCookie();

	if (token) {
		if (typeof Headers !== "undefined" && opts.headers instanceof Headers) {
			opts.headers = new Headers(opts.headers);
			opts.headers.set(CSRF_COOKIE_NAME, token);
		} else {
			opts.headers = Object.assign({}, opts.headers, { [CSRF_COOKIE_NAME]: token });
		}
	} else {
		/* 🔴 Chegou aqui: não havia cookie E a semeadura não produziu um. Esta
		   escrita VAI tomar 403 — e tem de aparecer NOMEADA, não como 403
		   misterioso três semanas depois. `log.error` morre no console de quem
		   sofreu (erro de cliente não chega ao log do servidor), por isso vai
		   também pro Sentry: na linha de acesso este 403 é indistinguível de
		   qualquer outro. */
		log.error("[csrf] write without token cookie, even after seeding: " + method + " " + path);
		Sentry.captureMessage("[csrf] write without token cookie", {
			level: "error",
			extra: { method, path, seedUseless: csrfSeedUseless }
		});
	}

	return fetch(appendPrefix(path), opts);
};

/* ── Repetir UMA vez quando o filtro recusou ──────────────────────────────────

   🔴 A janela que sobra do desenho: entre ler o cookie e a requisição sair, uma
   resposta do boot pode trocá-lo. O cabeçalho vai com o valor velho e o
   `Cookie:` com o novo — o Play compara os dois e recusa. É raro, é
   auto-curável, e sem repetição o usuário vê um erro que sumiria sozinho.

   🔴 POR QUE REPETIR É SEGURO, inclusive em rota NÃO idempotente: `csrf-01` sai
   de UM lugar só — `util.CustomCSRFErrorHandler`, o error handler do filtro de
   CSRF —, e o filtro recusa ANTES do controller. O código é prova de que a
   escrita não rodou. ⚠️ Se um dia esse código passar a ser emitido de outro
   ponto, esta repetição deixa de ser segura.                                    */

const CSRF_REFUSAL_CODE = "csrf-01";

const isCsrfRefusal = async (response) => {
	if (!response || response.status !== 403) return false;
	try {
		/* 🔴 `clone()` OBRIGATÓRIO: ler o corpo consome o stream, e o
		   `checkStatus` receberia vazio. Sem isto, um 403 que NÃO é de CSRF
		   perderia a mensagem e viraria erro genérico na tela. */
		const data = await response.clone().json();
		return data && data.code === CSRF_REFUSAL_CODE;
	} catch (err) {
		/* 403 sem corpo JSON existe (nginx, proxy). Não é recusa de CSRF. */
		return false;
	}
};

/* ⚠️ Corpo precisa ser RE-ENVIÁVEL. `FormData`, string e `Blob` o navegador
   re-serializa a cada `fetch`; `ReadableStream` NÃO — a segunda tentativa sairia
   com corpo vazio, 200, e em silêncio. Hoje o portal só usa os três primeiros;
   a guarda existe para que mudar isso falhe visível, não calado. */
const isReplayable = (body) =>
	!(typeof ReadableStream !== "undefined" && body instanceof ReadableStream);

/**
 * 🔴 O ÚNICO lugar do portal que fala com a API. Todo `fetch` da camada de
 * serviços passa por aqui — é o que garante o cabeçalho de CSRF em toda escrita,
 * e é por isso que `.cc-tools/check-csrf-transport.sh` reprova `fetch(` fora
 * deste arquivo.
 *
 * `path` vem SEM prefixo (`/schools/...`); o prefixo é aplicado aqui.
 */
export const apiFetch = async (path, options) => {
	const method = ((options && options.method) || "GET").toUpperCase();

	/* Método seguro não passa pelo filtro de CSRF — inclusive o `GET /config` da
	   semeadura, que por isso nunca cai na repetição abaixo. */
	if (CSRF_SAFE_METHODS.includes(method)) {
		return fetch(appendPrefix(path), Object.assign({}, options));
	}

	const response = await sendWithCsrf(path, options, method);

	if (!(await isCsrfRefusal(response))) return response;

	if (!isReplayable(options && options.body)) {
		log.error("[csrf] refusal on non-replayable body, not retrying: " + method + " " + path);
		return response;
	}

	/* ⚠️ EXATAMENTE uma repetição. Se o cookie estiver genuinamente velho ou
	   ausente — e não numa corrida —, a segunda tentativa falha igual; repetir em
	   laço transformaria um 403 em N. */
	log.warn("[csrf] refused; retrying once with the re-read cookie: " + method + " " + path);
	/* ⚠️ Nível WARNING, não error: isto se curou. Mas TEM de ser contado — se a
	   repetição for silenciosa, a janela some do radar e nunca se sabe se ela é
	   real ou teórica. É este número que diz se o conserto valeu. */
	Sentry.captureMessage("[csrf] refusal retried once", {
		level: "warning",
		extra: { method, path }
	});

	return sendWithCsrf(path, options, method);
};

export const checkStatus = async (response) => {

	//Initially, we set default HttpError with generic error message
	let error = new HttpError("GENERIC", response.status, `HTTP Error ${response.statusText}`);

	// 🔴 204/205 NÃO TÊM CORPO — e `response.json()` em corpo vazio lança
	// SyntaxError. Sem esta saída, o parse caía no catch abaixo e a promise
	// rejeitava com o HttpError genérico: a tela dizia que falhou, o F5 mostrava
	// que tinha funcionado. Valia pra todo DELETE que devolve `noContent()`.
	// ⚠️ `response.ok` é true aqui; o retorno é `null` porque não há corpo — o
	// caller de um 204 não deve ler nada da resposta.
	if (response.status === 204 || response.status === 205) {
		return null;
	}

	// Then we to process/parse response body in json format
	// If it fails, a standard Error/Exception will be thrown and handled in the catch statement
	try {
		
		let data;

		//if NOT an attachment, interpret response as json
		
        if (response.headers.get('Content-Disposition') && response.headers.get('Content-Disposition').indexOf('attachment') > -1) {
			data = await response.blob();
        } else {
			
			let contentType = response.headers.get('Content-Type') || '';
			let mimeType = contentType.split(';', 1)[0].trim().toLowerCase()
			
			switch (mimeType) {
  				case "text/plain":
				case "text/html":
    				data = await response.text();
    				break;
  				case "application/json":
				case "text/json":
  				default:
    				data = await response.json();
			}
		}
		
		if (response.ok) {
			return data;
		} else if (response.status >= 400 && response.status < 499) { //Http Client Error
			//Check if code attribute has been properly set
			if (data.code) {
				//Check if AuthError
				if (data.code.indexOf("auth-") !== -1) {
					error = new AuthError(data.code, response.status, data.message);
				} else { 
					error = new ValidationError(data.code, response.status, data.detail, data.message);
				}
			} 
	    }  else if (response.status >= 500 && response.status < 599) { //Http Server Error
			
			error = new HttpError(data.code, response.status, data.message);
	    } 

	} catch (err) {
		//Do nothing here. This block exists only to intercept json parse exception
		//Proper error handler should happen in block bellow
		
		log.error("backend-api.checkStatus: " + err);
	}
	
	//Finally Error is throw to be handled in upper level
	throw error;
}


export const objectToFormData = (obj, blackList) => {
	var fd = new FormData();
	var formKey;

	for(var property in obj) {
		if(obj.hasOwnProperty(property) && (!isArrayWithLength(blackList) || !blackList.includes(property))) {
			formKey = property;
			fd.append(formKey, obj[property] !== null && obj[property] !== undefined ? obj[property] : "");
		}
	}

	return fd;    
};

export const objectToQueryParameters = (obj, blackList) => {
	var queryString = "";
	var filterKey;

	for(var property in obj) {
		if(obj.hasOwnProperty(property) && (!isArrayWithLength(blackList) || !blackList.includes(property))) {
			/* 🔴 null/undefined NÃO viram parâmetro (2026-08-13).
			   `encodeURIComponent(null)` devolve a STRING "null", e nenhum
			   endpoint quer isso — as rotas declaram `?= null`, então ausente já
			   É o default.

			   O ciclo que isso causava: a 1ª carga não manda o filtro → o
			   servidor ECOA `active: null` na resposta → vira estado da página →
			   o refresh seguinte manda `active=null` → `@CheckBoolean` reprova
			   com 400. Ou seja, a primeira carga funcionava e o primeiro refresh
			   depois dela quebrava (salvar e voltar pra listagem é um refresh).

			   ⚠️ String vazia CONTINUA indo: `coreDisciplineKey=` é significativo
			   ("sem filtro") e os servidores já tratam. Só nulo sai. */
			if (obj[property] === null || obj[property] === undefined) continue;
			filterKey = property;

			/* 🔴 ARRAY vira `chave[]=a&chave[]=b`, NÃO `chave=a,b` (2026-09-06).
			   `encodeURIComponent(['A','B'])` devolve "A%2CB" — uma string com
			   vírgula —, e um parâmetro declarado `List[String]` na rota do Play
			   NÃO casa com isso: ele espera a chave repetida. O sintoma é filtro
			   que "não faz nada", sem erro nenhum, porque o valor chega como um
			   item só com vírgula dentro.
			   ⚠️ 14 rotas declaram `List[String]` hoje. O `messageApi` resolvia
			   isso sozinho, com blacklist + laço próprio — a segunda cópia seria
			   escrita agora, pro filtro de tipo do Log. Fica no ponto único.
			   ⚠️ Array VAZIO não emite nada: "nenhum selecionado" e "sem filtro"
			   são a mesma coisa pro servidor, e `chave[]=` mandaria uma string
			   vazia pra dentro da lista. */
			if (Array.isArray(obj[property])) {
				obj[property].forEach((item) => {
					if (item === null || item === undefined) return;
					queryString = queryString.concat(queryString ? "&" : "?",
						filterKey, '[]=', encodeURIComponent(item));
				});
				continue;
			}

			if (queryString) queryString = queryString.concat("&");
			else queryString = queryString.concat("?");
			queryString = queryString.concat(filterKey, '=', encodeURIComponent(obj[property]));
		}
	}

	return queryString;    
};